import { ref, toValue, watchEffect, type MaybeRefOrGetter, type Ref } from "vue";
import QRCode from "qrcode";

export interface UseQrCodeReturn {
  svg: Ref<string>;
  error: Ref<Error | null>;
  download: (filename?: string) => void;
}

/**
 * Generates an SVG QR code from a reactive source (ref, getter or plain string)
 * and re-generates it live whenever the source changes. The QR is rendered
 * entirely client-side — no network call, no external service.
 *
 * @param source - the URL/text to encode
 */
export function useQrCode(source: MaybeRefOrGetter<string>): UseQrCodeReturn {
  const svg = ref("");
  const error = ref<Error | null>(null);

  watchEffect(async () => {
    const value = toValue(source);
    if (!value) {
      svg.value = "";
      return;
    }

    try {
      svg.value = await QRCode.toString(value, {
        type: "svg",
        // Quiet zone (in modules) around the QR so it stays scannable once printed.
        margin: 4,
        errorCorrectionLevel: "M",
        color: { dark: "#000000", light: "#ffffff" },
      });
      error.value = null;
    } catch (err) {
      error.value = err instanceof Error ? err : new Error(String(err));
      svg.value = "";
    }
  });

  const download = (filename?: string): void => {
    if (!svg.value) return;

    const blob = new Blob([svg.value], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.download = `${filename || "qr-code"}.svg`;
    link.href = url;
    link.click();
    URL.revokeObjectURL(url);
  };

  return { svg, error, download };
}
